import React from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  applicationDetails,
  applicationStatusChange,
} from "../../services/applicationService";
import Indentification from "../../components/elements/Indentification";
import { initialsValue } from "../../helpers";
import Select from "../../components/form/front/Select";

const SkeletonDetails = () => (
  <div className="animate-pulse">
    <div className="h-64 bg-slate-200" />
    <div className="max-w-7xl mx-auto px-6 -mt-20 relative z-10 pb-16">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
            <div className="h-8 bg-slate-200 rounded w-3/4 mb-3" />
            <div className="h-5 bg-slate-100 rounded w-1/4 mb-6" />
            <div className="space-y-3">
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-full" />
              <div className="h-4 bg-slate-100 rounded w-4/5" />
            </div>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 border border-slate-200 h-72" />
        </div>
      </div>
    </div>
  </div>
);

const ApplicationDetailsPage = () => {
  const { applicationId } = useParams();
  const queryClient = useQueryClient();
  const { data: application, isLoading } = useQuery({
    queryKey: ["application-details", applicationId],
    queryFn: async () => {
      if (!applicationId) return null;
      const res = await applicationDetails({ id: applicationId });

      if (res?.status !== 200) {
        toast.warning(res?.message || "Failed to fetch application details");
        return null;
      }
      return res.data;
    },
    enabled: !!applicationId,
  });
  const statusOptions = [
    { value: "submitted", name: "Submitted" },
    { value: "reviewed", name: "Reviewed" },
    { value: "shortlisted", name: "Shortlisted" },
    { value: "hired", name: "Hired" },
    { value: "rejected", name: "Rejected" },
    { value: "withdrawn", name: "Withdrawn" },
  ];

  const { mutate: changeStatus, isPending: statusUpdating } = useMutation({
    mutationFn: async (status) => {
      const res = await applicationStatusChange({
        ids: [application?._id],
        status,
      });
      return res;
    },
    onSuccess: (res, status) => {
      if (res?.status === 200) {
        toast.success("Status updated successfully");

        queryClient.setQueryData(
          ["application-details", applicationId],
          (old) => ({
            ...old,
            status,
          }),
        );
      } else {
        toast.error(res?.message || "Failed to update status");
      }
    },
    onError: () => {
      toast.error("Failed to update status");
    },
  });
  if (isLoading) return <SkeletonDetails />;

  if (!application)
    return (
      <div className="p-20 text-center font-sans text-slate-600">
        Application not found.
      </div>
    );

  const {
    applicant,
    address,
    education,
    workExperience,
    references,
    softSkills,
    confidentialInfo,
    attachments,
    organization,
    jobDetail,
    status,
    createdAt,
  } = application;

  return (
    <div className="min-h-screen">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Application Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Applicant Header Card */}
            <div className="bg-white rounded-xl p-6 shadow">
              {/* TOP BAR */}
              <div className="flex items-center justify-between mb-6">
                {/* LEFT SIDE */}
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">
                    {applicant?.firstName}
                  </h1>

                  <div className="">
                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-envelope text-denim"></i>
                      <span>{applicant.email}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <i className="fa-solid fa-phone text-denim"></i>
                      <span>
                        {applicant.phone.fullNumber
                          ? applicant.phone.fullNumber
                          : applicant.phone.countryCode +
                            " " +
                            applicant.phone.number}
                      </span>
                    </div>
                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className="flex items-center gap-4">
                  {/* STATUS */}
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-slate-400 uppercase mb-1">
                      Application Status
                    </span>
                    <Select
                      placeholder="Status"
                      dropdownData={statusOptions}
                      selectedValue={status}
                      onChange={(value) => changeStatus(value)}
                      xPlacement="bottom-right"
                      dropdownClass="bg-white w-full"
                      dropdownButtonClass="shadow-sm dark:border-gray-700 dark:bg-gray-700 h-9 rounded-full shadow-[0_0_2px_rgba(0,0,0,0.16)]"
                      selectPlaceholder="text-zinc-700"
                      selectDropdownIcon="text-zinc-700"
                      disabled={statusUpdating}
                    />

                    {/* <select
                      value={status}
                      disabled={statusUpdating}
                      onChange={(e) => changeStatus(e.target.value)}
                      className="h-9 px-3 text-sm border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-denim">
                      {statusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select> */}
                  </div>

                  {/* AVATAR */}
                  <Indentification
                    initial={initialsValue(
                      `${applicant.firstName} ${applicant.lastName}`,
                    )}
                    size="xl"
                    type="circle"
                    className="shadow"
                  />
                </div>
              </div>

              {/* APPLIED DATE */}
              <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 text-xs font-medium w-fit">
                <i className="fa-solid fa-calendar text-xs"></i>
                <span>Applied: {new Date(createdAt).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Job Applied For */}
            <div className="bg-white rounded-xl p-6 space-y-6 shadow">
              <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <i className="fa-solid fa-briefcase text-denim"></i>
                Position Applied For
              </h3>
              <div className="mb-3">
                <h4 className="text-lg font-semibold text-slate-700">
                  {jobDetail?.content?.en?.title}
                </h4>
                <div className="text-slate-600 text-sm">
                  {organization?.[0]?.content?.en?.title}
                </div>
              </div>
            </div>

            {/* Education */}
            <div className="bg-white rounded-xl p-6 shadow">
              <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                <i className="fa-solid fa-graduation-cap text-denim"></i>
                Education
              </h3>
              {education.map((edu, index) => (
                <div
                  key={index}
                  className="mb-4 last:mb-0 pb-4 last:pb-0 border-b last:border-0 border-slate-100"
                >
                  <div className="font-semibold text-slate-800">
                    {edu.degree}
                  </div>
                  <div className="text-slate-600">{edu.instituteName}</div>
                  <div className="text-sm text-slate-500">
                    {edu.type} • Graduated: {edu.yearPass}
                  </div>
                </div>
              ))}
            </div>

            {/* Work Experience */}
            {workExperience.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-briefcase text-denim"></i>
                  Work Experience
                </h3>
                {workExperience.map((work, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <div className="font-semibold text-slate-800">
                      {work.jobTitle}
                    </div>
                    <div className="text-slate-600">{work.employer}</div>
                    <div className="text-sm text-slate-500">
                      {new Date(work.startDate).toLocaleDateString()} -{" "}
                      {new Date(work.endDate).toLocaleDateString()}
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      Supervisor: {work.supervisorName} • {work.supervisorPhone}
                    </div>
                    {work.certifications && (
                      <div className="text-sm text-slate-600 mt-2">
                        <i className="fa-solid fa-certificate text-amber-600 mr-1"></i>
                        Certifications: {work.certifications}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Soft Skills */}
            {softSkills?.questions?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-comments text-denim"></i>
                  Questionnaire
                </h3>
                {softSkills.questions.map((q, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <div className="font-semibold text-slate-700 mb-2">
                      {q.question}
                    </div>
                    <div className="text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {q.answer}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* References */}
            {references.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow">
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fa-solid fa-user-group text-denim"></i>
                  References
                </h3>
                {references.map((ref, index) => (
                  <div
                    key={index}
                    className="mb-4 last:mb-0 pb-4 last:pb-0 border-b last:border-0 border-slate-100"
                  >
                    <div className="font-semibold text-slate-800">
                      {ref.name}
                    </div>
                    <div className="text-slate-600">
                      {ref.title} at {ref.company}
                    </div>
                    <div className="text-sm text-slate-500">
                      Relationship: {ref.relationship}
                    </div>
                    <div className="flex gap-4 mt-2 text-sm text-slate-600">
                      <span>
                        <i className="fa-solid fa-phone mr-1"></i>
                        {ref.phone}
                      </span>
                      <span>
                        <i className="fa-solid fa-envelope mr-1"></i>
                        {ref.email}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6 sticky top-6">
            {/* Applicant Info Card */}
            <div className="bg-white rounded-xl p-6 shadow">
              <h4 className="font-bold text-lg mb-5 text-slate-800 flex items-center gap-2">
                <i className="fa-solid fa-circle-info text-denim"></i>
                Applicant Info
              </h4>

              <div className="space-y-4">
                {Object.values(address || {}).some(Boolean) && (
                  <div className="pb-4 border-b border-slate-100">
                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                      Address
                    </div>
                    <div className="text-slate-800 text-sm">
                      {[address?.address1, address?.address2]
                        .filter(Boolean)
                        .join(", ")}
                      {[address?.address1, address?.address2].filter(Boolean)
                        .length > 0 && <br />}
                      {[address?.city, address?.state, address?.zipcode]
                        .filter(Boolean)
                        .join(", ")}
                      {[address?.city, address?.state, address?.zipcode].filter(
                        Boolean,
                      ).length > 0 && <br />}
                      {address?.country ?? ""}
                    </div>
                  </div>
                )}

                {applicant.dateAvailable && (
                  <div className="pb-4 border-b border-slate-100">
                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                      Date Available
                    </div>
                    <div className="text-slate-800 font-medium">
                      {new Date(applicant.dateAvailable).toLocaleDateString()}
                    </div>
                  </div>
                )}

                <div className="pb-4 border-b border-slate-100">
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                    Willing to Relocate
                  </div>
                  <div className="text-slate-800 font-medium">
                    {applicant.willingToRelocate ? (
                      <span className="text-green-600">
                        <i className="fa-solid fa-check mr-1"></i>Yes
                      </span>
                    ) : (
                      <span className="text-red-600">
                        <i className="fa-solid fa-times mr-1"></i>No
                      </span>
                    )}
                  </div>
                </div>

                {applicant.availability?.length > 0 && (
                  <div>
                    <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-1.5">
                      Availability
                    </div>
                    {applicant.availability.map((avail, index) => (
                      <div key={index} className="text-slate-800 text-sm">
                        {avail?.day}: {avail?.hours}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Attachments */}
            {attachments?.length > 0 && (
              <div className="bg-white rounded-xl p-6 shadow">
                <h4 className="font-bold text-lg mb-4 text-slate-800 flex items-center gap-2">
                  <i className="fa-solid fa-paperclip text-denim"></i>
                  Attachments
                </h4>
                {attachments.map((file, index) => (
                  <a
                    key={index}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-lg bg-white hover:bg-slate-50 transition-colors border border-slate-200 mb-2 last:mb-0"
                  >
                    <i className="fa-solid fa-file-pdf text-red-600 text-xl"></i>
                    <div className="flex-1">
                      <div className="text-slate-800 font-medium text-sm">
                        {file.filename}
                      </div>
                      <div className="text-slate-500 text-xs">
                        {(parseInt(file.size) / 1024).toFixed(2)} KB
                      </div>
                    </div>
                    <i className="fa-solid fa-external-link text-slate-400"></i>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetailsPage;
